import styled, { css } from 'styled-components';

import {
  zIndexBackgroundLayer1,
  zIndexContentLayer2,
} from '@xing-com/layout-tokens';
import { Link } from '@xing-com/link';
import {
  motionEasingEnter,
  motionTimeXXS,
  scale010,
  scale110,
  scale130,
  scale150,
  spaceXXS,
  xdlColorA11yBorder,
  xdlColorBackground,
  xdlColorFocusRing,
  xdlColorSelected,
  xdlColorText,
  xdlColorTextInvert,
} from '@xing-com/tokens';

import type { ClosingIndicatorSize } from '.';

type ClosingIndicatorTProps = {
  $size?: ClosingIndicatorSize;
};

// *** Sizes ***
export const sizes = {
  medium: css`
    width: ${scale130};
    height: ${scale130};
  `,
  large: css`
    width: ${scale150};
    height: ${scale150};
  `,
};

/** This moved the focus ring manually to the inner button. */
const fakeFocus = css<ClosingIndicatorTProps>`
  ${(props) => css`
    ${props.$size &&
    props.$size === 'large' &&
    css`
      outline-offset: -7px;
    `};
  `};
`;

// *** Base ***
const baseButton = css<ClosingIndicatorTProps>`
  align-items: center;
  background-color: transparent;
  border: none;
  color: ${xdlColorText};
  display: inline-flex;
  justify-content: center;
  margin: 0;
  padding: 0;
  position: relative;
  text-decoration: none;
  vertical-align: top;
  z-index: ${zIndexContentLayer2};

  ${(props) => css`
    ${props.$size && sizes[props.$size]};
  `};

  &::after {
    background-color: ${xdlColorBackground};
    border-color: ${xdlColorA11yBorder};
    border-radius: 50%;
    border-style: solid;
    border-width: ${scale010};
    box-sizing: border-box;
    content: '';
    cursor: pointer;
    height: ${scale110};
    position: absolute;
    transition-duration: ${motionTimeXXS};
    transition-property: transform, background-color;
    transition-timing-function: ${motionEasingEnter};
    width: ${scale110};
    z-index: ${zIndexBackgroundLayer1};
  }

  &:hover {
    &::after {
      transform: scale(1.1667);
    }
  }

  &:active {
    &::after {
      transform: scale(1);
    }
  }

  &:hover,
  &:active,
  &:focus {
    color: ${xdlColorTextInvert};
    cursor: pointer;
    text-decoration: none;

    &::after {
      background-color: ${xdlColorSelected};
    }
  }

  &:focus {
    border-radius: 50%;
    outline-offset: -${spaceXXS};
    outline: ${scale010} solid ${xdlColorFocusRing};
    ${fakeFocus};
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    border-radius: 50%;
    outline-offset: -${spaceXXS};
    outline: ${scale010} solid ${xdlColorFocusRing};
    ${fakeFocus};
  }

  &:disabled {
    cursor: default;
  }

  &:disabled::after {
    cursor: default;
    user-select: none;
  }
`;

// *** Components ***
export const ClosingIndicator = styled.button<ClosingIndicatorTProps>`
  ${baseButton};
`;

export const ClosingIndicatorLink = styled(Link)<ClosingIndicatorTProps>`
  ${baseButton};
`;
